/**
 * 🏢 Enterprise Project Service
 *
 * Supabase CRUD for the enterprise projects table + project_members.
 * Also fetches dashboard metrics via the project_dashboard_metrics view.
 *
 * Rules:
 *  - Always filter by organization_id
 *  - Never call Supabase directly in components
 *  - Respect RLS and multi-tenancy
 */

import { supabase } from '../lib/supabaseClient';
import { notifyProjectAssigned } from './notificationProducer';
import type {
  EnterpriseProject,
  ProjectStatus,
  ProjectMember,
  ProjectMemberRole,
  OperationalRole,
  ProjectDashboardMetrics,
  VisibilityScope,
  ProjectHealth,
  ProjectAttachment,
} from '../types/enterpriseProject';

// ─── Project Filters ────────────────────────────────────────

export interface ProjectFilters {
  status?: ProjectStatus;
  owner_id?: string;
  search?: string;
  visibility_scope?: VisibilityScope;
}

// ─── CRUD ───────────────────────────────────────────────────

export class ProjectService {
  /**
   * List all projects for an organization.
   */
  static async list(organizationId: string, filters?: ProjectFilters): Promise<EnterpriseProject[]> {
    let query = supabase
      .from('projects')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.owner_id) query = query.eq('owner_id', filters.owner_id);
    if (filters?.visibility_scope) query = query.eq('visibility_scope', filters.visibility_scope);
    if (filters?.search) query = query.ilike('name', `%${filters.search}%`);

    const { data, error } = await query;
    if (error) {
      console.warn('[ProjectService.list] Query failed (migration pending?):', error.message);
      return [];
    }
    return (data ?? []) as EnterpriseProject[];
  }

  /**
   * Get a single project by ID.
   */
  static async getById(organizationId: string, projectId: string): Promise<EnterpriseProject | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', projectId)
      .single();

    if (error) throw error;
    return data as EnterpriseProject | null;
  }

  /**
   * Create a new project. Automatically adds creator as owner member.
   */
  static async create(
    organizationId: string,
    userId: string,
    project: {
      name: string;
      description?: string;
      status?: ProjectStatus;
      start_date?: string;
      target_date?: string;
      visibility_scope?: VisibilityScope;
    },
  ): Promise<EnterpriseProject> {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        organization_id: organizationId,
        owner_id: userId,
        created_by: userId,
        name: project.name,
        nome_projeto: project.name,
        description: project.description ?? null,
        status: project.status ?? 'draft',
        start_date: project.start_date ?? null,
        target_date: project.target_date ?? null,
        visibility_scope: project.visibility_scope ?? 'organization',
        progress: 0,
      })
      .select()
      .single();

    if (error) throw error;

    // Auto-add creator as owner member
    await supabase.from('project_members').insert({
      project_id: data.id,
      user_id: userId,
      role: 'owner',
    });

    return data as EnterpriseProject;
  }

  /**
   * Update project fields.
   */
  static async update(
    organizationId: string,
    projectId: string,
    updates: Partial<Pick<EnterpriseProject, 'name' | 'description' | 'status' | 'start_date' | 'target_date' | 'progress' | 'visibility_scope'>>,
  ): Promise<EnterpriseProject> {
    const { data, error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('organization_id', organizationId)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data as EnterpriseProject;
  }

  /**
   * Delete a project (cascades to members).
   */
  static async delete(organizationId: string, projectId: string): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', projectId);

    if (error) throw error;
  }

  // ─── Members ────────────────────────────────────────────────

  /**
   * List members of a project.
   */
  static async listMembers(projectId: string): Promise<ProjectMember[]> {
    const { data, error } = await supabase
      .from('project_members')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });

    if (error) {
      console.warn('[ProjectService.listMembers] RLS or query error:', error.code, error.message);
      return [];
    }
    const members = (data ?? []) as ProjectMember[];
    const userIds = [...new Set(members.map(member => member.user_id).filter(Boolean))];
    if (userIds.length === 0) return members;

    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('user_id, name, email')
      .in('user_id', userIds);

    const profilesById = new Map((profiles ?? []).map(profile => [profile.user_id, profile]));
    return members.map(member => {
      const profile = profilesById.get(member.user_id);
      return {
        ...member,
        user_name: profile?.name ?? member.user_name ?? null,
        user_email: profile?.email ?? member.user_email ?? null,
      };
    });
  }

  /**
   * Add a member to a project.
   */
  static async addMember(
    projectId: string,
    userId: string,
    role: ProjectMemberRole = 'viewer',
    operationalRole?: OperationalRole | null,
  ): Promise<ProjectMember> {
    const { data, error } = await supabase
      .from('project_members')
      .insert({
        project_id: projectId,
        user_id: userId,
        role,
        ...(operationalRole ? { operational_role: operationalRole } : {}),
      })
      .select()
      .single();

    if (error) throw error;
    return data as ProjectMember;
  }

  /**
   * Update a member's role.
   */
  static async updateMemberRole(memberId: string, role: ProjectMemberRole): Promise<void> {
    const { error } = await supabase
      .from('project_members')
      .update({ role })
      .eq('id', memberId);

    if (error) throw error;
  }

  /**
   * Remove a member from a project.
   */
  static async removeMember(memberId: string): Promise<void> {
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('id', memberId);

    if (error) throw error;
  }

  /**
   * Check if a user is a member of a project.
   */
  static async isMember(projectId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('project_members')
      .select('id')
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .limit(1);

    if (error) return false;
    return (data?.length ?? 0) > 0;
  }

  /**
   * Assert that a user has access to a project. Throws if not.
   * Returns the user's role in the project.
   */
  static async assertUserInProject(
    projectId: string,
    userId: string,
  ): Promise<{ role: ProjectMemberRole; canEdit: boolean; canView: boolean }> {
    const { data, error } = await supabase
      .from('project_members')
      .select('role')
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      throw new Error('Acesso negado: usuário não é membro do projeto');
    }

    const role = data.role as ProjectMemberRole;
    return {
      role,
      canEdit: role === 'owner' || role === 'editor',
      canView: true,
    };
  }

  /**
   * Add a member and send notification.
   */
  static async addMemberWithNotification(
    projectId: string,
    userId: string,
    organizationId: string,
    projectName: string,
    role: ProjectMemberRole = 'viewer',
  ): Promise<ProjectMember> {
    const member = await ProjectService.addMember(projectId, userId, role);
    notifyProjectAssigned({
      userId,
      organizationId,
      projectName,
      role,
      projectId,
    });
    return member;
  }

  // ─── Dashboard Metrics ──────────────────────────────────────

  /**
   * Get dashboard metrics for a single project.
   */
  static async getDashboardMetrics(organizationId: string, projectId: string): Promise<ProjectDashboardMetrics | null> {
    const { data, error } = await supabase
      .from('project_dashboard_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .single();

    if (error) return null;
    return data as ProjectDashboardMetrics;
  }

  /**
   * Get dashboard metrics for all projects in an org.
   */
  static async listDashboardMetrics(organizationId: string): Promise<ProjectDashboardMetrics[]> {
    const { data, error } = await supabase
      .from('project_dashboard_metrics')
      .select('*')
      .eq('organization_id', organizationId);

    if (error) {
      console.warn('[ProjectService.listDashboardMetrics] Query failed (migration pending?):', error.message);
      return [];
    }
    return (data ?? []) as ProjectDashboardMetrics[];
  }

  // ─── Aggregated queries ─────────────────────────────────────

  /**
   * Get recent activity for a project (last 10 updates across assets).
   */
  static async getRecentActivity(organizationId: string, projectId: string): Promise<RecentActivityItem[]> {
    const items: RecentActivityItem[] = [];

    // Recent actions
    const { data: actions } = await supabase
      .from('actions')
      .select('id, title, status, updated_at')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false })
      .limit(5);

    if (actions) {
      for (const a of actions) {
        items.push({
          id: a.id,
          type: 'action',
          title: a.title,
          status: a.status,
          updated_at: a.updated_at,
        });
      }
    }

    // Recent issues
    const { data: issues } = await supabase
      .from('issues')
      .select('id, title, status, updated_at')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false })
      .limit(5);

    if (issues) {
      for (const i of issues) {
        items.push({
          id: i.id,
          type: 'problem',
          title: i.title,
          status: i.status,
          updated_at: i.updated_at,
        });
      }
    }

    // Sort by most recent
    return items
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 10);
  }

  // ─── Health ─────────────────────────────────────────────────

  /**
   * Compute project health from existing metrics (client-side, no DB call).
   * - critical: overdue AND has open problems
   * - warning: overdue OR has open problems
   * - healthy: on track
   */
  static computeHealth(metrics: ProjectDashboardMetrics, project: EnterpriseProject): ProjectHealth {
    const isOverdue =
      !!project.target_date &&
      new Date(project.target_date) < new Date() &&
      project.status !== 'completed' &&
      project.status !== 'archived';

    if (isOverdue && metrics.open_problems > 0) return 'critical';
    if (isOverdue || metrics.open_problems > 0) return 'warning';
    return 'healthy';
  }

  // ─── Real Progress ───────────────────────────────────────────

  /**
   * Compute real progress from done/completed actions and issues linked to the project.
   * Returns 0–100 (integer).
   */
  static async getRealProgress(organizationId: string, projectId: string): Promise<number> {
    const [{ data: actions }, { data: issues }] = await Promise.all([
      supabase
        .from('actions')
        .select('status')
        .eq('organization_id', organizationId)
        .eq('project_id', projectId),
      supabase
        .from('issues')
        .select('status')
        .eq('organization_id', organizationId)
        .eq('project_id', projectId),
    ]);

    const allItems = [...(actions ?? []), ...(issues ?? [])];
    if (allItems.length === 0) return 0;

    const done = allItems.filter(
      (i) =>
        i.status === 'completed' ||
        i.status === 'done' ||
        i.status === 'closed' ||
        i.status === 'cancelled',
    ).length;

    return Math.round((done / allItems.length) * 100);
  }

  // ─── Hub: project-scoped item lists ─────────────────────────

  /**
   * Hub: list actions linked to a project.
   */
  static async listProjectActions(organizationId: string, projectId: string): Promise<ProjectHubAction[]> {
    const { data, error } = await supabase
      .from('actions')
      .select('id, title, status, priority, due_date, assigned_to, updated_at')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false })
      .limit(50);

    if (error) {
      console.warn('[ProjectService.listProjectActions]', error.message);
      return [];
    }
    return (data ?? []) as ProjectHubAction[];
  }

  /**
   * Hub: list issues (problems) linked to a project.
   */
  static async listProjectIssues(organizationId: string, projectId: string): Promise<ProjectHubIssue[]> {
    const { data, error } = await supabase
      .from('issues')
      .select('id, title, status, severity, updated_at')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false })
      .limit(50);

    if (error) {
      console.warn('[ProjectService.listProjectIssues]', error.message);
      return [];
    }
    return (data ?? []) as ProjectHubIssue[];
  }

  /**
   * Hub: list workflow executions linked to a project.
   */
  static async listProjectExecutions(organizationId: string, projectId: string): Promise<ProjectHubExecution[]> {
    const { data, error } = await supabase
      .from('workflow_instances_v9')
      .select('id, status, started_at, completed_at, metadata')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('started_at', { ascending: false })
      .limit(50);

    if (error) {
      console.warn('[ProjectService.listProjectExecutions]', error.message);
      return [];
    }
    return (data ?? []) as ProjectHubExecution[];
  }

  /**
   * Hub: list blueprints (process maps) linked to a project.
   */
  static async listProjectBlueprints(organizationId: string, projectId: string): Promise<ProjectHubBlueprint[]> {
    const { data, error } = await supabase
      .from('process_blueprints')
      .select('id, name, description, status, version, created_at')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.warn('[ProjectService.listProjectBlueprints]', error.message);
      return [];
    }
    return (data ?? []) as ProjectHubBlueprint[];
  }

  // ─── Attachments ─────────────────────────────────────────────

  static readonly ATTACHMENT_BUCKET = 'project-files';

  /**
   * List all attachments for a project.
   */
  static async listAttachments(projectId: string): Promise<ProjectAttachment[]> {
    const { data, error } = await supabase
      .from('project_attachments')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      if (error.message?.includes('project_attachments')) {
        // Migration pending — table doesn't exist yet, silence
      } else {
        console.warn('[ProjectService.listAttachments]', error.message);
      }
      return [];
    }
    return (data ?? []) as ProjectAttachment[];
  }

  /**
   * Upload a file to Supabase Storage and record it in project_attachments.
   */
  static async uploadAttachment(
    projectId: string,
    organizationId: string,
    uploadedBy: string,
    file: File,
  ): Promise<ProjectAttachment> {
    const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `${organizationId}/${projectId}/${Date.now()}_${sanitized}`;

    const { error: storageError } = await supabase.storage
      .from(ProjectService.ATTACHMENT_BUCKET)
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (storageError) throw new Error(`Upload falhou: ${storageError.message}`);

    const { data: urlData } = supabase.storage
      .from(ProjectService.ATTACHMENT_BUCKET)
      .getPublicUrl(filePath);

    const { data, error: dbError } = await supabase
      .from('project_attachments')
      .insert({
        project_id: projectId,
        organization_id: organizationId,
        name: file.name,
        file_url: urlData.publicUrl,
        file_path: filePath,
        file_type: file.type || 'application/octet-stream',
        file_size: file.size,
        uploaded_by: uploadedBy,
      })
      .select()
      .single();

    if (dbError) throw new Error(`Falha ao registrar arquivo: ${dbError.message}`);
    return data as ProjectAttachment;
  }

  /**
   * Delete an attachment: removes from Storage and DB.
   */
  static async deleteAttachment(attachmentId: string, filePath: string): Promise<void> {
    await supabase.storage.from(ProjectService.ATTACHMENT_BUCKET).remove([filePath]);

    const { error } = await supabase
      .from('project_attachments')
      .delete()
      .eq('id', attachmentId);

    if (error) throw error;
  }

  // ─── Auth helper ─────────────────────────────────────────────

  /**
   * Get the current authenticated user ID.
   * Encapsulates auth.getUser() so pages don't import supabase directly.
   */
  static async getCurrentUserId(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');
    return user.id;
  }
}

// ─── Supporting Types ───────────────────────────────────────

export interface RecentActivityItem {
  id: string;
  type: 'action' | 'problem' | 'workflow' | 'indicator';
  title: string;
  status: string;
  updated_at: string;
}

export interface ProjectHubAction {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assigned_to: string | null;
  updated_at: string;
}

export interface ProjectHubIssue {
  id: string;
  title: string;
  status: string;
  severity: string;
  updated_at: string;
}

export interface ProjectHubExecution {
  id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  metadata: Record<string, unknown> | null;
}

export interface ProjectHubBlueprint {
  id: string;
  name: string;
  description: string | null;
  status: string | null;
  version: string | null;
  created_at: string;
}
